import { useRef, useEffect, useCallback } from 'react';

interface AdaptivePollingOptions {
    /** Minimum interval in ms (default: 30000 = 30s) */
    minInterval: number;
    /** Maximum interval in ms (default: 600000 = 10min) */
    maxInterval: number;
    /** Multiplier when no new data (default: 1.5) */
    backoffMultiplier: number;
    /** Whether polling is active */
    enabled: boolean;
}

const DEFAULT_OPTIONS: AdaptivePollingOptions = {
    minInterval: 30000,
    maxInterval: 600000,
    backoffMultiplier: 1.5,
    enabled: false,
};

/**
 * Adaptive polling with exponential backoff.
 *
 * - Starts at minInterval (30s)
 * - No new data → interval *= backoffMultiplier (slows down)
 * - New data arrives → reset to minInterval (speeds up)
 * - Never exceeds maxInterval (10min)
 *
 * Progression with no data (1.5x): 30s → 45s → 67s → 101s → 152s → 228s → 342s → 513s → 600s (cap)
 *
 * @param pollFn - Returns true if new data, false otherwise
 * @param options - Config
 */
export const useAdaptivePolling = (
    pollFn: () => Promise<boolean>,
    options: Partial<AdaptivePollingOptions> = {},
): void => {
    const config = { ...DEFAULT_OPTIONS, ...options };
    const currentIntervalRef = useRef<number>(config.minInterval);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pollFnRef = useRef(pollFn);

    pollFnRef.current = pollFn;

    const clearPollTimeout = useCallback((): void => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const scheduleNextPoll = useCallback((): void => {
        clearPollTimeout();

        timeoutRef.current = setTimeout(async () => {
            try {
                const hasNewData = await pollFnRef.current();

                if (hasNewData) {
                    currentIntervalRef.current = config.minInterval;
                } else {
                    currentIntervalRef.current = Math.min(
                        currentIntervalRef.current * config.backoffMultiplier,
                        config.maxInterval,
                    );
                }
            } catch {
                currentIntervalRef.current = Math.min(
                    currentIntervalRef.current * config.backoffMultiplier,
                    config.maxInterval,
                );
            }

            scheduleNextPoll();
        }, currentIntervalRef.current);
    }, [config.minInterval, config.maxInterval, config.backoffMultiplier, clearPollTimeout]);

    useEffect(() => {
        if (!config.enabled) {
            clearPollTimeout();
        } else {
            (async (): Promise<void> => {
                try {
                    const hasNewData = await pollFnRef.current();

                    if (hasNewData) {
                        currentIntervalRef.current = config.minInterval;
                    }
                } catch {
                    // Silently handle
                }
                scheduleNextPoll();
            })();
        }

        return (): void => {
            clearPollTimeout();
        };
    }, [config.enabled, scheduleNextPoll, clearPollTimeout, config.minInterval]);
};
